import {SetStateAction, useCallback, useRef, useState} from 'react';

type StateSetter<T, CommitArgs = any> = (
  newState: SetStateAction<T>,
  shouldCommit?: boolean,
  commitArgs?: CommitArgs,
) => void;

/**
 * A hook for managing state that is mirrored from controlled props.
 * This is useful for when you want to keep track of controlled state, but don't want to commit it every time.
 * The returned setter accepts a second parameter to commit the state, it defaults to false.
 *
 * - The state is updated immediately when the controlled prop changes.
 * - The state can be optionally committed via a callback.
 *
 * Examples:
 *  - QueryEntrySection takes in `entries` as a prop, but only commits entry changes when they are actually valid via its `onUpdate` prop.
 *
 * @param prop - The value to mirror, usually a prop.
 * @param commit - The callback that is called to commit the state, usually a callback prop.
 */
export function useStagedState<T, CommitOptions = any>(
  prop: T,
  commit?: (state: T, opts?: CommitOptions) => void,
): [T, StateSetter<T, CommitOptions>] {
  // for initial render, we use the prop value directly
  let [state, setState] = useState(prop);
  const previousProp = useRef(prop);

  // when the prop's value changes, we need to update the state
  // this is the recommended way to do this: https://legacy.reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops
  if (prop !== previousProp.current) {
    setState(prop);
    // point state to the incoming prop value, this is needed so that
    // the current render has the most up to date value and it is not
    // delayed until the next render
    state = prop;
    previousProp.current = prop;
  }

  const stateSetter = useCallback<StateSetter<T>>(
    (newState, shouldCommit, commitOptions) => {
      setState((oldState) => {
        const finalState = newState instanceof Function ? newState(oldState) : newState;

        if (shouldCommit) {
          commit?.(finalState, commitOptions);
        }

        return finalState;
      });
    },
    [state, commit],
  );

  return [state, stateSetter];
}
